/**
 * Workforce Cases Page
 *
 * Unified case management for workforce operations:
 * - Recruitment cases (candidate tracking)
 * - Training cases (curriculum progress)
 * - Certification cases (lifecycle tracking)
 * - Supervision cases (staff management)
 * - Onboarding cases (new hire tracking)
 *
 * Clicking a case navigates to the full CaseDetail view (8 tabs).
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  Search,
  Clock,
  User,
  FileText,
  Users,
  Award,
  BookOpen,
  UserCheck,
  ArrowLeft,
  MessageSquare,
  ListTodo,
  X,
  Layers,
  ToggleLeft,
  ToggleRight,
  ChevronRight,
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { PageHeader, PageContainer, PageState } from '@/components/weavein';
import { useAppTranslation } from '@/contexts/TranslationContext';
import CaseDetail from './CaseDetail';
import { authFetch } from '@/lib/authFetch';

const getAuthToken = (): string | null => {
  return localStorage.getItem('auth_token');
};

interface Case {
  case_id: number;
  case_number: string;
  case_type: string;
  case_status: string;
  title: string;
  subject_name: string | null;
  priority: string;
  opened_date: string | null;
  target_resolution_date: string | null;
  pending_tasks: number;
  notes_count: number;
  days_open: number;
  assigned_to_name: string | null;
  program_id: number | null;
  program_name?: string | null;
}

interface Program {
  program_id: number;
  program_name: string;
}

export default function CasesPage() {
  const { t } = useAppTranslation();
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterPriority, setFilterPriority] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null);

  // Programs for create modal
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loadingPrograms, setLoadingPrograms] = useState(false);

  // Create case form state
  const [newCase, setNewCase] = useState({
    case_type: 'recruitment',
    title: '',
    description: '',
    priority: 'normal',
    program_id: null as number | null,
    auto_start_workflow: true,
  });

  const caseTypeIcons: Record<string, React.ReactNode> = {
    recruitment: <Users className="w-4 h-4" />,
    training: <BookOpen className="w-4 h-4" />,
    certification: <Award className="w-4 h-4" />,
    supervision: <UserCheck className="w-4 h-4" />,
    onboarding: <User className="w-4 h-4" />,
  };

  const caseTypeColors: Record<string, string> = {
    recruitment: 'bg-blue-100 text-blue-700',
    training: 'bg-purple-100 text-purple-700',
    certification: 'bg-emerald-100 text-emerald-700',
    supervision: 'bg-orange-100 text-orange-700',
    onboarding: 'bg-pink-100 text-pink-700',
  };

  const statusColors: Record<string, string> = {
    open: 'bg-blue-100 text-blue-700',
    in_progress: 'bg-yellow-100 text-yellow-700',
    pending_review: 'bg-purple-100 text-purple-700',
    resolved: 'bg-emerald-100 text-emerald-700',
    closed: 'bg-paper-2 text-ink-700',
    cancelled: 'bg-red-100 text-red-700',
  };

  const priorityColors: Record<string, string> = {
    low: 'text-ink-500',
    normal: 'text-blue-500',
    high: 'text-orange-500',
    urgent: 'text-red-500',
  };

  useEffect(() => {
    fetchCases();
  }, [filterType, filterStatus, filterPriority]);

  const fetchCases = async () => {
    const token = getAuthToken();
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const params = new URLSearchParams();
      if (filterType) params.append('case_type', filterType);
      if (filterStatus) params.append('case_status', filterStatus);
      if (filterPriority) params.append('priority', filterPriority);
      params.append('limit', '100');

      const res = await authFetch(
        getWeaveinApiUrl(`/api/workforce/cases?${params.toString()}`),
      );

      if (res.ok) {
        const data = await res.json();
        setCases(data.items || []);
      }
    } catch (error) {
      console.error('Error fetching cases:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPrograms = async () => {
    const token = getAuthToken();
    if (!token) return;

    setLoadingPrograms(true);
    try {
      const res = await authFetch(getWeaveinApiUrl('/api/programs'));
      if (res.ok) {
        const data = await res.json();
        setPrograms(data.programs || data || []);
      }
    } catch (error) {
      console.error('Error fetching programs:', error);
    } finally {
      setLoadingPrograms(false);
    }
  };

  const createCase = async () => {
    const token = getAuthToken();
    if (!token) return;

    try {
      const payload: Record<string, unknown> = {
        case_type: newCase.case_type,
        title: newCase.title,
        description: newCase.description,
        priority: newCase.priority,
      };
      if (newCase.program_id) {
        payload.program_id = newCase.program_id;
        payload.auto_start_workflow = newCase.auto_start_workflow;
      }

      const res = await authFetch(getWeaveinApiUrl('/api/workforce/cases'), {
        method: 'POST',
        jsonBody: payload,
      });

      if (res.ok) {
        const created = await res.json();
        setShowCreateModal(false);
        setNewCase({
          case_type: 'recruitment',
          title: '',
          description: '',
          priority: 'normal',
          program_id: null,
          auto_start_workflow: true,
        });
        fetchCases();
        // Navigate directly to the new case detail
        if (created.case_id) {
          setSelectedCaseId(created.case_id);
        }
      }
    } catch (error) {
      console.error('Error creating case:', error);
    }
  };

  const handleOpenCreateModal = () => {
    fetchPrograms();
    setShowCreateModal(true);
  };

  const filteredCases = cases.filter(c =>
    c.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.case_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.subject_name && c.subject_name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  // If a case is selected, show the full CaseDetail view
  if (selectedCaseId) {
    return (
        <CaseDetail
          caseId={selectedCaseId}
          onBack={() => {
            setSelectedCaseId(null);
            fetchCases(); // Refresh list when coming back
          }}
        />
    );
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Workforce · Cases"
        leading={
          <Link to="/workforce/dashboard" className="text-ink-400 hover:text-ink">
            <ArrowLeft className="w-5 h-5" />
          </Link>
        }
        title={t('workforce.cases.title', 'Case Management')}
        description={t('workforce.cases.subtitle', 'Track recruitment, training, and certification cases')}
        actions={
          <button
            onClick={handleOpenCreateModal}
            className="inline-flex items-center gap-1.5 rounded-md bg-v2accent px-3 py-1.5 text-xs font-medium text-white"
          >
            <Plus className="w-4 h-4" />
            {t('workforce.cases.newCase', 'New Case')}
          </button>
        }
      >
        {/* Search and Filters */}
        <div className="flex items-center space-x-3 px-4 pb-3.5 sm:px-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-ink-400" />
            <input
              type="text"
              placeholder={t('workforce.cases.search', 'Search cases...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-v2border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-v2border rounded-lg focus:ring-2 focus:ring-emerald-500"
          >
            <option value="">{t('workforce.cases.allTypes', 'All Types')}</option>
            <option value="recruitment">Recruitment</option>
            <option value="training">Training</option>
            <option value="certification">Certification</option>
            <option value="supervision">Supervision</option>
            <option value="onboarding">Onboarding</option>
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-v2border rounded-lg focus:ring-2 focus:ring-emerald-500"
          >
            <option value="">{t('workforce.cases.allStatuses', 'All Statuses')}</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="pending_review">Pending Review</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </PageHeader>

      {/* Cases List */}
      <PageContainer>
          {loading ? (
            <PageState variant="loading" />
          ) : filteredCases.length === 0 ? (
            <PageState
              variant="empty"
              title={t('workforce.cases.noCases', 'No cases found')}
              action={
                <button
                  onClick={handleOpenCreateModal}
                  className="inline-flex items-center gap-1.5 rounded-md border border-v2border bg-surface px-2.5 py-1.5 text-xs font-medium text-ink hover:bg-paper-2"
                >
                  {t('workforce.cases.createFirst', 'Create your first case')}
                </button>
              }
            />
          ) : (
            <div className="divide-y divide-v2border-muted">
              {filteredCases.map((caseItem) => (
                <div
                  key={caseItem.case_id}
                  onClick={() => setSelectedCaseId(caseItem.case_id)}
                  className="px-6 py-4 bg-surface hover:bg-paper cursor-pointer transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <div className={`p-2 rounded-lg ${caseTypeColors[caseItem.case_type] || 'bg-paper-2'}`}>
                        {caseTypeIcons[caseItem.case_type] || <FileText className="w-4 h-4" />}
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-ink-400">{caseItem.case_number}</span>
                          <span className={`w-2 h-2 rounded-full ${priorityColors[caseItem.priority]?.replace('text-', 'bg-')}`} />
                        </div>
                        <p className="font-medium text-ink mt-0.5">{caseItem.title}</p>
                        <div className="flex items-center space-x-2 mt-0.5">
                          {caseItem.subject_name && (
                            <p className="text-sm text-ink-500">{caseItem.subject_name}</p>
                          )}
                          {caseItem.program_name && (
                            <>
                              {caseItem.subject_name && <span className="text-ink-300">|</span>}
                              <span className="text-xs text-indigo-600 flex items-center">
                                <Layers className="w-3 h-3 mr-1" />
                                {caseItem.program_name}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="text-right">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[caseItem.case_status] || 'bg-paper-2'}`}>
                          {caseItem.case_status.replace('_', ' ')}
                        </span>
                        <div className="flex items-center space-x-3 mt-2 text-xs text-ink-400">
                          <span className="flex items-center">
                            <Clock className="w-3 h-3 mr-1" />
                            {caseItem.days_open}d
                          </span>
                          <span className="flex items-center">
                            <ListTodo className="w-3 h-3 mr-1" />
                            {caseItem.pending_tasks}
                          </span>
                          <span className="flex items-center">
                            <MessageSquare className="w-3 h-3 mr-1" />
                            {caseItem.notes_count}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-ink-400" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
      </PageContainer>

      {/* Create Case Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface rounded-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-ink">Create New Case</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-ink-400 hover:text-ink-500">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">Case Type</label>
                <select
                  value={newCase.case_type}
                  onChange={(e) => setNewCase({ ...newCase, case_type: e.target.value })}
                  className="w-full px-3 py-2 border border-v2border rounded-lg focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="recruitment">Recruitment</option>
                  <option value="training">Training</option>
                  <option value="certification">Certification</option>
                  <option value="supervision">Supervision</option>
                  <option value="onboarding">Onboarding</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">Title</label>
                <input
                  type="text"
                  value={newCase.title}
                  onChange={(e) => setNewCase({ ...newCase, title: e.target.value })}
                  placeholder="Case title..."
                  className="w-full px-3 py-2 border border-v2border rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">Description</label>
                <textarea
                  value={newCase.description}
                  onChange={(e) => setNewCase({ ...newCase, description: e.target.value })}
                  placeholder="Case description..."
                  rows={3}
                  className="w-full px-3 py-2 border border-v2border rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">Priority</label>
                <select
                  value={newCase.priority}
                  onChange={(e) => setNewCase({ ...newCase, priority: e.target.value })}
                  className="w-full px-3 py-2 border border-v2border rounded-lg focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              {/* Program Selection */}
              <div className="border-t border-v2border pt-4">
                <label className="block text-sm font-medium text-ink-700 mb-1">
                  <Layers className="w-4 h-4 inline mr-1" />
                  Link to Program (Optional)
                </label>
                <select
                  value={newCase.program_id ?? ''}
                  onChange={(e) => setNewCase({
                    ...newCase,
                    program_id: e.target.value ? parseInt(e.target.value) : null,
                  })}
                  className="w-full px-3 py-2 border border-v2border rounded-lg focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">No Program</option>
                  {loadingPrograms ? (
                    <option disabled>Loading programs...</option>
                  ) : (
                    programs.map((p) => (
                      <option key={p.program_id} value={p.program_id}>
                        {p.program_name}
                      </option>
                    ))
                  )}
                </select>
                <p className="text-xs text-ink-400 mt-1">
                  Linking to a program enables workflow tracking and form integration
                </p>
              </div>

              {/* Auto-start Workflow Toggle */}
              {newCase.program_id && (
                <div className="flex items-center justify-between bg-paper rounded-lg p-3">
                  <div>
                    <p className="text-sm font-medium text-ink-700">Auto-start Program Workflow</p>
                    <p className="text-xs text-ink-500">
                      Automatically create workflow from program template
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setNewCase({ ...newCase, auto_start_workflow: !newCase.auto_start_workflow })}
                    className="flex-shrink-0"
                  >
                    {newCase.auto_start_workflow ? (
                      <ToggleRight className="w-8 h-8 text-emerald-600" />
                    ) : (
                      <ToggleLeft className="w-8 h-8 text-ink-400" />
                    )}
                  </button>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 border border-v2border rounded-lg text-ink-700 hover:bg-paper"
              >
                Cancel
              </button>
              <button
                onClick={createCase}
                disabled={!newCase.title.trim()}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                Create Case
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
